export const ROW_TYPES = [
  'TEXT',
  'NUMBER',
  'INTEGER',
  'BOOLEAN',
  'ARRAY',
  'B64'
] as const
export type RowType = (typeof ROW_TYPES)[number]

export interface SchemaRow {
  name: string
  type: RowType
  required?: boolean
  description?: string
}

export function rowTypeFor(prop: Record<string, unknown>): RowType {
  const type = String(prop.type ?? 'string').toLowerCase()
  if (type === 'number') return 'NUMBER'
  if (type === 'integer') return 'INTEGER'
  if (type === 'boolean') return 'BOOLEAN'
  if (type === 'array') return 'ARRAY'
  if (String(prop.format ?? '').toLowerCase() === 'b64') return 'B64'
  return 'TEXT'
}

function propFor(row: SchemaRow): Record<string, unknown> {
  const prop: Record<string, unknown> = {}
  if (row.type === 'NUMBER') prop.type = 'number'
  else if (row.type === 'INTEGER') prop.type = 'integer'
  else if (row.type === 'BOOLEAN') prop.type = 'boolean'
  else if (row.type === 'ARRAY') prop.type = 'array'
  else prop.type = 'string'
  if (row.type === 'B64') prop.format = 'b64'
  if (row.description) prop.description = row.description
  return prop
}

export function schemaToRows(
  schema: Record<string, unknown> | undefined
): SchemaRow[] {
  const properties = (schema?.properties ?? {}) as Record<
    string,
    Record<string, unknown>
  >
  const required = new Set(
    Array.isArray(schema?.required) ? (schema.required as string[]) : []
  )
  return Object.entries(properties)
    .filter(([key]) => key !== 'tool')
    .map(([key, prop]) => ({
      name: key,
      type: rowTypeFor(prop),
      required: required.has(key),
      description:
        typeof prop.description === 'string' ? prop.description : undefined
    }))
}

export function rowsToSchema(
  rows: SchemaRow[],
  withRequired: boolean
): Record<string, unknown> {
  const properties: Record<string, unknown> = {}
  for (const row of rows) properties[row.name.trim()] = propFor(row)
  const schema: Record<string, unknown> = { type: 'object', properties }
  if (withRequired) {
    const required = rows.filter((r) => r.required).map((r) => r.name.trim())
    if (required.length) schema.required = required
  }
  return schema
}

export function validateRows(rows: SchemaRow[], label: string): string {
  const seen = new Set<string>()
  for (const row of rows) {
    const rowName = row.name.trim()
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(rowName)) {
      return `${label}: invalid name "${rowName || '(empty)'}" — use letters/digits/underscore`
    }
    if (rowName === 'tool') return `${label}: "tool" is a reserved name`
    if (seen.has(rowName)) return `${label}: duplicate name "${rowName}"`
    seen.add(rowName)
  }
  return ''
}
